<template>
  <v-container fluid>
    <v-card :loading="loadSkeleton">
      <v-card-title class="d-flex justify-between space-between" style="justify-content: space-between;">
        <span class="text-h6">Gestión de Galerías</span>
        <v-btn-toggle v-model="viewMode" mandatory>
          <v-btn value="grid" icon title="Vista de Mosaico">
            <v-icon>mdi-view-grid</v-icon>
          </v-btn>
          <v-btn value="list" icon title="Vista de Lista">
            <v-icon>mdi-view-list</v-icon>
          </v-btn>
        </v-btn-toggle>
      </v-card-title>

      <div class="d-flex justify-space-between align-center mt-4 px-2 space-between">
        <v-pagination
          v-model="page"
          :length="Math.ceil(totalItems / itemsPerPage)"
          :total-visible="7"
          :loading="true"
        ></v-pagination>
        <v-select
          v-model="itemsPerPage"
          :items="[8, 16, 32, 64, 128]"
          variant="outlined"
          label="Ítems por página"
          dense
          style="max-width: 100px"
          @update:modelValue="onItemsPerPageChange"
        />
      </div>

      <v-tabs v-model="tab" align-tabs="center" fixed-tabs grow @click="openTab(tab)">
        <v-tab value="HOME"><b>HOME</b></v-tab>
        <v-tab value="CRIS"><b>CRIS</b></v-tab>
      </v-tabs>

      <v-tabs-window v-model="tab">
        <v-tabs-window-item
          v-for="(images, type) in groupedGallery"
          :key="type"
          :value="type"
        >
          <v-container fluid>
            <v-row v-if="viewMode === 'grid'">
              <v-col
                v-for="gallery in images"
                :key="gallery.id"
                cols="12" sm="6" md="4" lg="3"
              >
                <v-skeleton-loader :loading="loadSkeleton" height="240" type="image, list-item-two-line">
                  <v-responsive>
                    <v-card>
                      <v-img
                        :src="getImagePath(gallery.image)"
                        height="200px"
                        cover
                      />
                      <v-card-title class="text-subtitle-1">#{{gallery.id}} {{ gallery.title }}</v-card-title>
                      <v-card-subtitle>{{ gallery.category }}</v-card-subtitle>
                      <v-card-text>
                        {{ cortarTexto(gallery.description, 40) }}
                        <v-tooltip activator="parent" location="top">{{gallery.description}}</v-tooltip>
                      </v-card-text>
                      <v-card-subtitle><strong>Filtro :</strong> {{ gallery.filter }}</v-card-subtitle>
                      <v-card-subtitle><strong>Clase adicional :</strong> {{ gallery.adicional_class || 'N/A' }}</v-card-subtitle>
                      <v-card-subtitle><strong>Estado :</strong> {{ gallery.status === 'A' ? 'Activo' : 'Inactivo' }}</v-card-subtitle>
                      <v-card-actions>
                        <v-btn icon="mdi-eye" @click="previewImage(getImagePath(gallery.image))" />
                        <v-spacer />
                        <v-btn icon="mdi-pencil" color="primary" @click="openEditDialog(gallery)" />
                        <v-spacer />
                        <v-btn icon="mdi-delete" color="red" @click="confirmDelete(gallery.id)" />
                      </v-card-actions>
                    </v-card>
                  </v-responsive>
                </v-skeleton-loader>
              </v-col>
            </v-row>

            <v-row v-else>
              <v-col cols="12">
                <v-skeleton-loader :loading="loadSkeleton" type="article">
                  <v-table density="compact" class="mt-4" style="width: -webkit-fill-available;">
                    <thead>
                      <tr>
                        <th>id</th>
                        <th>Imagen</th>
                        <th>Categoría</th>
                        <th>Título</th>
                        <th>Filtro</th>
                        <th>Clase</th>
                        <th>Estado</th>
                        <th>Descripción</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="gallery in images" :key="gallery.id">
                        <td>{{ gallery.id }}</td>
                        <td style="width: 120px">
                          <v-img :src="getImagePath(gallery.image)" height="60" cover />
                        </td>
                        <td>{{ gallery.category }}</td>
                        <td>{{ gallery.title }}</td>
                        <td>{{ gallery.filter }}</td>
                        <td>{{ gallery.adicional_class }}</td>
                        <td>{{ gallery.status }}</td>
                        <td>
                          {{ cortarTexto(gallery.description, 20) }}
                          <v-tooltip activator="parent" location="top">{{gallery.description}}</v-tooltip>
                        </td>
                        <td>
                          <v-menu>
                            <template v-slot:activator="{ props }">
                              <v-btn icon="mdi-dots-vertical" variant="text" v-bind="props"></v-btn>
                            </template>
                            <v-card>
                              <v-list>
                                <v-list-subheader>OPCIONES</v-list-subheader>
                                <v-list-item @click="previewImage(getImagePath(gallery.image))">
                                  <v-list-item-title>Ver</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="openEditDialog(gallery)">
                                  <v-list-item-title>Editar</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="confirmDelete(gallery.id)">
                                  <v-list-item-title>Eliminar</v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-card>
                          </v-menu>
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-skeleton-loader>
              </v-col>
            </v-row>
          </v-container>
        </v-tabs-window-item>
      </v-tabs-window>
    </v-card>

    <!-- FAB flotantes -->
    <div class="fab-container">
      <v-fab
        icon="mdi-reload"
        size="small"
        color="primary"
        @click="cargarGaleria()"
      />
      <v-fab
        icon="mdi-plus"
        size="small"
        color="secondary"
        @click="openUploadDialog(tab)"
      />
    </div>

    <!-- Diálogo de previsualización -->
    <v-dialog v-model="dialogPreview" max-width="800" scrollable>
      <v-card>
        <v-img :src="previewUrl" height="auto" cover />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogPreview = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de formulario -->
    <v-dialog v-model="dialogEdit" max-width="800px" persistent>
      <v-card>
        <v-card-title>{{ isEdit ? 'Editar Imagen' : 'Agregar Imagen' }}</v-card-title>
        <v-card-text>
          <GalleryForm
            v-model="formData"
            :isEdit="isEdit"
            :loading="loader"
            @submit="editarGaleria"
            @submit2="guardarGaleria"
            @cancel="() => dialogEdit = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Diálogo de confirmación -->
    <v-dialog v-model="dialogConfirm" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h6">¿Está seguro que desea eliminar la imagen con ID <b>#{{ itemToDelete }}</b>?</v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn color="red" text @click="performDelete">Eliminar</v-btn>
          <v-btn color="grey" text @click="dialogConfirm = false">Cancelar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import GalleryForm from '@/components/GalleryForm.vue'
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { HOST_URL_IMGS } from '@/utils/constantes'
import { GalleryItem } from '@/interfaces/gallery.interface'

const store = useStore()
const gallerys = computed(() => store.state.Gallery.galleryList)
const statusUpdateGallery = computed(() => store.state.Gallery.statusUpdateGallery)
const statusInsertGallery = computed(() => store.state.Gallery.statusInsertGallery)
const statusDelGallery = computed(() => store.state.Gallery.statusDelGallery)

const dialogConfirm = ref(false)
const itemToDelete = ref<number | null>(null)
const gallery = ref<GalleryItem[]>([])
const dialogPreview = ref(false)
const previewUrl = ref('')
const newImage = ref<File | null>(null)
const uploadType = ref<string>('')
const viewMode = ref<'grid' | 'list'>('grid')
const dialogEdit = ref(false)
const tab = ref<string>('HOME')
const loader = ref(false)
const snackbar = ref(false)
const loadSkeleton = ref(false)

const totalItems = computed(() => store.state.Gallery.totalItems)
const page = ref(1)
const itemsPerPage = ref(8)
const isEdit = ref(false)

const formData = ref<GalleryItem>({
  id: 0,
  category: '',
  type: '',
  title: '',
  image: '',
  filter: '',
  adicional_class: 'NONE',
  description: '',
  created_at: '',
  updated_at: '',
  status: 'A',
})
//CRUD START
async function cargarGaleria() {
  loadSkeleton.value = true
  await store.dispatch('Gallery/fetchGallery', {
    params: {
      page: page.value,
      limit: itemsPerPage.value,
      type: tab.value,
    }
  })
  gallery.value = gallerys.value?.data.items ?? []
  loadSkeleton.value = false
}

async function guardarGaleria({ data, imageFile }: { data: GalleryItem; imageFile: File | null }) {
  loader.value = true
  const formData = new FormData()
  formData.append('adicional_class', data.adicional_class ?? '')
  formData.append('category', data.category)
  formData.append('description', data.description)
  formData.append('filter', data.filter ?? '')
  formData.append('status', data.status ?? 'A')
  formData.append('title', data.title)
  formData.append('type', data.type)
  if (imageFile) {
    formData.append('image', imageFile)
  } else if (data.image && !data.image.startsWith('http')) {
    try {
      const image = await urlToFile(getImagePath(data.image), data.image)
      formData.append('image', image)
    } catch (error) {
      console.error('Error convirtiendo imagen existente en archivo:', error)
    }
  }
  await store.dispatch('Gallery/postGallery', formData)
  loader.value = false
  snackbar.value = true
  if (statusInsertGallery.value?.success) {
    dialogEdit.value = false
    cargarGaleria()
  }
}

async function editarGaleria({ data, imageFile }: { data: GalleryItem; imageFile: File | null }) {
  loader.value = true
  const formData = new FormData()
  formData.append('adicional_class', data.adicional_class ?? '')
  formData.append('category', data.category)
  formData.append('description', data.description)
  formData.append('filter', data.filter ?? '')
  formData.append('status', data.status ?? 'A')
  formData.append('title', data.title)
  formData.append('type', data.type)
  if (imageFile) {
    formData.append('image', imageFile)
  } else if (data.image && !data.image.startsWith('http')) {
    try {
      const image = await urlToFile(getImagePath(data.image), data.image)
      formData.append('image', image)
    } catch (error) {
      console.error('Error convirtiendo imagen existente en archivo:', error)
    }
  }
  await store.dispatch('Gallery/putGallery', {
    id: data.id,
    formData
  })
  loader.value = false
  if (statusUpdateGallery.value?.success) {
    dialogEdit.value = false
    cargarGaleria()
  }
}

async function deleteImage(id: number) {
  await store.dispatch('Gallery/delGallery', id)
  loader.value = false
  if (statusDelGallery.value?.success) {
    dialogConfirm.value = false
    cargarGaleria()
  }
}
//CRUD END

function openUploadDialog(type: string) {
  isEdit.value = false
  uploadType.value = type
  formData.value = {
    id: 0,
    title: '',
    category: '',
    type: type,
    image: '',
    filter: '',
    adicional_class: 'NONE',
    description: '',
    created_at: '',
    updated_at: '',
    status: 'A'
  }
  dialogEdit.value = true
  newImage.value = null
}

const groupedGallery = computed(() => {
  if (!gallery.value || !Array.isArray(gallery.value)) return {}
  return gallery.value.reduce<Record<string, GalleryItem[]>>((acc, item) => {
    if (!acc[item.type]) acc[item.type] = []
    acc[item.type].push(item)
    return acc
  }, {})
})

function getImagePath(path: string) {
  const baseURL = `${HOST_URL_IMGS}/public/imgs/output`
  return path.startsWith('http') ? path : baseURL + '/' + path
}

function previewImage(url: string) {
  previewUrl.value = url
  dialogPreview.value = true
}

watch(page, () => {
  cargarGaleria()
})


function openEditDialog(g: GalleryItem) {
  isEdit.value = true
  formData.value = { ...g }
  dialogEdit.value = true
}

function cortarTexto(texto: string, max = 10) {
  return texto.length > max ? texto.slice(0, max) + '...' : texto
}

function openTab(newTab: string) {
  tab.value = newTab
  itemsPerPage.value = 8
  page.value = 1
  cargarGaleria()
}

async function urlToFile(url: string, filename: string, mimeType = 'image/jpeg'): Promise<File> {
  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
  })
  const blob = await response.blob()
  return new File([blob], filename.split('/').pop() || 'image.jpg', { type: mimeType })
}

const confirmDelete = (id: number) => {
  itemToDelete.value = id
  dialogConfirm.value = true
}

const onItemsPerPageChange = () => {
  page.value = 1
  cargarGaleria()
}

const performDelete = () => {
  if (itemToDelete.value !== null) {
    deleteImage(itemToDelete.value)
    dialogConfirm.value = false
    itemToDelete.value = null
  }
}

onMounted(() => {
  cargarGaleria()
})
</script>

<style scoped>
.fab-container {
  position: fixed;
  bottom: 24px;
  left: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 9999;
}
</style>
